import { AppMediaKindEnum, UploadMediaRequestDtoMediaKindEnum } from 'api/generated';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import { Button } from 'react-admin';
import { CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { api } from 'api';
import { http } from 'services';
import styled from 'styled-components';
import { useState } from 'react';

interface Props {
  handleClose: () => void;
  getFiles: () => void;
}

type FormValues = {
  files: File[];
  mediaKind: UploadMediaRequestDtoMediaKindEnum;
};

const AddMediaFileForm = ({ handleClose, getFiles }: Props) => {
  const { control, handleSubmit, setValue } = useForm<FormValues>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [progress, setProgress] = useState<number>(0);

  const uploadFile = (url: string, values: any, callback?: (...arg: any) => any) => {
    const file = new Blob([values], { type: 'application/octet-stream' });
    return http.put(url, file, {
      headers: {
        'content-type': 'application/octet-stream',
        'Access-Control-Allow-Origin': '*',
        Accept: '*/*',
      },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / values.size);
        callback && callback(percent > 100 ? 100 : percent);
      },
    });
  };

  const onSubmit = async (data: FormValues) => {
    const { mediaKind } = data;
    const file = data.files[0];
    setIsLoading(true);
    try {
      const response: any = await api.V1AdminsAppMediaStorageApi.appMediaStorageControllerGetSignedUrl({
        filename: file.name,
        mediaKind: mediaKind,
      });
      const url = response.data.signedUrl;
      uploadFile(url, file, (value) => setProgress(value)).then(() => {
        handleClose();
        getFiles();
        setIsLoading(false);
      });
    } catch (error: any) {
      if (error.response.status == 422) {
        setError('Данное имя файла уже используется');
      }
      console.log('error', error.message);
      setIsLoading(false);
    }
  };

  const handleChange = (e: any) => {
    setValue('files', e.target.files);
    setError('');
  };

  return (
    <Box pt={5} width={{ xs: '100vW', sm: 500 }} mt={{ xs: 2, sm: 1 }}>
      <Stack direction="row" p={2} pl={5}>
        <Typography variant="h5" flex="1">
          Добавить файл
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <input type="file" required onChange={handleChange} />
        <Controller
          name="mediaKind"
          control={control}
          render={({ field }) => (
            <FormControl variant="filled" sx={{ minWidth: 120 }}>
              <InputLabel>Тип Медиа Файла</InputLabel>
              <Select {...field} required>
                <MenuItem value={AppMediaKindEnum.MainPageFirstVideo}>{AppMediaKindEnum.MainPageFirstVideo}</MenuItem>
                <MenuItem value={AppMediaKindEnum.MainPageSecondVideo}>{AppMediaKindEnum.MainPageSecondVideo}</MenuItem>
              </Select>
            </FormControl>
          )}
        />
        {error && <Typography color="red">{error}</Typography>}
        {isLoading && (
          <Container>
            <CircularProgress variant="determinate" value={progress} />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Typography variant="caption" component="div" color="text.secondary">
                {progress}
              </Typography>
            </Box>
          </Container>
        )}
        <Button type="submit" label="Загрузить" color="secondary" disabled={isLoading} />
      </StyledForm>
    </Box>
  );
};

export default AddMediaFileForm;

const StyledForm = styled.form`
  width: 90%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
`;

const Container = styled.div`
  margin: 10px auto;
  position: relative;
  display: inline-flex;
`;
